/**
 * MailItemsListPage — `/mail/items` paged list of mail items, filtered by
 * status + tracking number. Wires to `GET /v1/mail-items` (operator-scoped).
 *
 * No screens-equivalent file — derived from `MailDashboard.jsx`'s table to give
 * the operator console a dedicated all-items list (the dashboard shows the 10
 * most recent only).
 */
import { type JSX, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { listMailItems } from '../../lib/api/mail-items';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { FilterBar, MailItemTable } from './shared/MailUI';

const STATUS_OPTIONS = [
  '',
  'DRAFT',
  'PENDING_PAYMENT',
  'REGISTERED',
  'IN_SORTING',
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERED',
  'RETURNED',
  'DAMAGED',
  'LOST',
] as const;

export function MailItemsListPage(): JSX.Element {
  const [status, setStatus] = useState<string>('');
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [page, setPage] = useState<number>(1);

  const items = useQuery({
    queryKey: ['mail', 'items', 'list', { status, trackingNumber, page }],
    queryFn: () =>
      listMailItems({
        ...(status !== '' && { status }),
        ...(trackingNumber.trim() !== '' && { trackingNumber: trackingNumber.trim() }),
        page,
        pageSize: 25,
      }),
    retry: false,
    staleTime: 15_000,
  });

  const denied =
    items.isError && items.error instanceof ApiError && (items.error.status === 401 || items.error.status === 403);

  const total = items.data?.total ?? 0;
  const pageSize = items.data?.pageSize ?? 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div>
      <PageHeader
        title="Mail Items"
        description="Operator-scoped registered mail items."
      />

      <FilterBar
        searchValue={trackingNumber}
        onSearchChange={(v) => {
          setTrackingNumber(v);
          setPage(1);
        }}
        searchPlaceholder="Search by tracking number…"
      >
        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
          aria-label="Status filter"
          className="px-3 py-2 text-sm border border-border rounded-lg bg-white"
        >
          {STATUS_OPTIONS.map((s) => (
            <option key={s || 'all'} value={s}>
              {s === '' ? 'All statuses' : s.replace(/_/g, ' ')}
            </option>
          ))}
        </select>
      </FilterBar>

      {items.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          Your account does not have permission to view mail items. Please contact your administrator.
        </DeferredNotice>
      ) : items.isError ? (
        <DeferredNotice title="Service unreachable">
          {(items.error as Error).message}
        </DeferredNotice>
      ) : (
        <>
          <MailItemTable items={items.data?.items ?? []} />
          <div className="flex items-center justify-between mt-4 text-xs text-muted">
            <span>
              Page {page} of {totalPages} · {total} items
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="px-3 py-1.5 border border-border rounded-lg disabled:opacity-50 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
